// src/pages/Search.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import {
  SearchContainer,
  ContentContainer,
  SearchBox,
  SearchInput,
  ResultsTable,
  SearchButton,
  HelpText,
  HintRow,
  Note,
  FieldChip,
  SearchForm,
  FormGrid,
  FormField,
  FormPanel
} from '../styles/SearchStyles';
import { PageTitle } from '../styles/SharedStyles';
import { useSearch, EMPTY_FILTERS } from '../hooks/useSearch';
import SearchTableRow from '../components/features/search/TableRow';
import { LoadingSpinner, ErrorMessage, Th } from '../styles/HomeStyles';
import { apiService } from '../services/api';

// Build the list of visible page numbers with ellipsis gaps
const buildPageRange = (current, total) => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current]);
  if (current > 1) pages.add(current - 1);
  if (current < total) pages.add(current + 1);

  const sorted = Array.from(pages).sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('...');
    result.push(sorted[i]);
  }
  return result;
};

const Pagination = ({ currentPage, totalPages, totalResults, pageSize, onPageChange }) => {
  if (totalPages <= 1) return null;

  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalResults);
  const pageRange = buildPageRange(currentPage, totalPages);

  const btnStyle = (active, disabled) => ({
    padding: '0.4rem 0.75rem',
    margin: '0 0.15rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    backgroundColor: active ? '#3498db' : disabled ? '#f5f5f5' : 'white',
    color: active ? 'white' : disabled ? '#aaa' : '#333',
    fontWeight: active ? '600' : 'normal',
  });

  return (
    <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
      <span style={{ fontSize: '0.9rem', color: '#555' }}>
        Showing {start}–{end} of {totalResults.toLocaleString()} results
      </span>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', gap: '0.1rem' }}>
        <button
          style={btnStyle(false, currentPage === 1)}
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          ← Prev
        </button>

        {pageRange.map((item, idx) =>
          item === '...' ? (
            <span key={`ellipsis-${idx}`} style={{ padding: '0 0.3rem', color: '#888' }}>…</span>
          ) : (
            <button
              key={item}
              style={btnStyle(item === currentPage, false)}
              onClick={() => onPageChange(item)}
            >
              {item}
            </button>
          )
        )}

        <button
          style={btnStyle(false, currentPage === totalPages)}
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          Next →
        </button>
      </div>
    </div>
  );
};

const CustomTagInput = ({ pendingFilters, setPendingFilters, predefinedTags }) => {
  const [customTag, setCustomTag] = useState('');

  const addTag = () => {
    const trimmed = customTag.trim().toLowerCase();
    if (!trimmed || pendingFilters.tag.includes(trimmed) || predefinedTags.includes(trimmed)) return;
    setPendingFilters(f => ({ ...f, tag: [...f.tag, trimmed] }));
    setCustomTag('');
  };

  return (
    <div style={{ display: 'flex', gap: '0.35rem', marginTop: '0.25rem', alignItems: 'center' }}>
      <input
        type="text"
        placeholder="Custom tag…"
        value={customTag}
        onChange={e => setCustomTag(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
        style={{ padding: '0.3rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem', width: '130px' }}
      />
      <button
        type="button"
        onClick={addTag}
        style={{ padding: '0.3rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer', background: 'white' }}
      >
        Add
      </button>
      {pendingFilters.tag.filter(t => !predefinedTags.includes(t)).map(t => (
        <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', padding: '0.15rem 0.5rem', background: '#f3f4f6', borderRadius: '9999px', fontSize: '0.78rem', color: '#374151' }}>
          {t}
          <button
            type="button"
            onClick={() => setPendingFilters(f => ({ ...f, tag: f.tag.filter(x => x !== t) }))}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', color: '#6b7280', fontWeight: '700', lineHeight: 1 }}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
};

const FILTER_LABELS = {
  status: 'Status',
  packer: 'Packer',
  frosting: 'Frosting',
  tag: 'Tag',
  minutes: 'Last N min',
};

const activeFilterCount = (filters) =>
  Object.entries(filters).filter(([, v]) =>
    Array.isArray(v) ? v.length > 0 : v !== '' && v !== null && v !== undefined
  ).length;

const FORM_FIELDS = [
  { key: 'sha256', label: 'SHA256' },
  { key: 'md5', label: 'MD5' },
  { key: 'package_name', label: 'Package Name' },
  { key: 'app_name', label: 'App Name' },
  { key: 'malware_family', label: 'Malware Family' },
  { key: 'tag', label: 'Tag' },
  { key: 'status', label: 'Status' },
  { key: 'packer', label: 'Packer' },
  { key: 'cert', label: 'Certificate SHA1' },
];

const EMPTY_FORM_FIELDS = Object.fromEntries(FORM_FIELDS.map(f => [f.key, '']));

const quoteIfNeeded = (value) =>
  /[\s&|"]/.test(value) ? `"${value.replace(/"/g, '')}"` : value;

const buildQueryFromFields = (fields) =>
  Object.entries(fields)
    .filter(([, v]) => v.trim() !== '')
    .map(([k, v]) => `${k}:${quoteIfNeeded(v.trim())}`)
    .join(' ');

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    analyses,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    currentPage,
    totalResults,
    totalPages,
    pageSize,
    filters,
    setFilters,
    resetFilters,
    performSearch,
    fetchList,
    goToPage,
  } = useSearch();

  // Local input state (not committed until form submit)
  const [inputValue, setInputValue] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [pendingFilters, setPendingFilters] = useState(EMPTY_FILTERS);
  const [predefinedTags, setPredefinedTags] = useState([]);
  const [searchMode, setSearchMode] = useState('query'); // 'query' | 'form'
  const [formFields, setFormFields] = useState(EMPTY_FORM_FIELDS);


  // Load predefined tags once
  useEffect(() => {
    apiService.getPredefinedTags().then(data => setPredefinedTags(data.tags || [])).catch(() => { });
  }, []);

  // On mount: restore search/page/filters from URL params, then fetch
  useEffect(() => {
    const urlQuery = searchParams.get('q') || '';
    const urlPage = parseInt(searchParams.get('page') || '1', 10);
    const urlFilters = {
      status: searchParams.get('status') || '',
      packer: searchParams.get('packer') || '',
      frosting: searchParams.get('frosting') || '',
      tag: searchParams.getAll('tag'),
      minutes: searchParams.get('minutes') || '',
    };

    setInputValue(urlQuery);
    setPendingFilters(urlFilters);

    if (urlQuery) {
      performSearch(urlQuery, urlPage);
    } else {
      setFilters(urlFilters);
      fetchList(urlPage, urlFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep URL in sync whenever page/query/filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (currentPage > 1) params.set('page', String(currentPage));
    for (const [key, value] of Object.entries(filters)) {
      if (Array.isArray(value)) {
        value.forEach(v => params.append(key, v));
      } else if (value !== '') {
        params.set(key, String(value));
      }
    }
    setSearchParams(params, { replace: true });
  }, [searchQuery, currentPage, filters, setSearchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    performSearch(inputValue, 1);
  };

  const handleClear = () => {
    setInputValue('');
    fetchList(1);
  };

  const handleFormSearch = (e) => {
    e.preventDefault();
    const built = buildQueryFromFields(formFields);
    setInputValue(built);
    performSearch(built, 1);
  };

  const handleClearForm = () => {
    setFormFields(EMPTY_FORM_FIELDS);
  };


  const handleApplyFilters = () => {
    setFilters(pendingFilters);
    fetchList(1, pendingFilters);
    setFiltersOpen(false);
  };

  const handleClearFilters = () => {
    setPendingFilters(EMPTY_FILTERS);
    resetFilters();
    fetchList(1, EMPTY_FILTERS);
    setFiltersOpen(false);
  };

  const handleDismissChip = (key) => {
    const newFilters = { ...filters, [key]: '' };
    setFilters(newFilters);
    setPendingFilters(newFilters);
    fetchList(1, newFilters);
  };

  const handleDismissTag = (tagToRemove) => {
    const newFilters = { ...filters, tag: filters.tag.filter(t => t !== tagToRemove) };
    setFilters(newFilters);
    setPendingFilters(newFilters);
    fetchList(1, newFilters);
  };

  const handlePageChange = (page) => {
    goToPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const activeCount = activeFilterCount(filters);

  const selectStyle = {
    padding: '0.4rem 0.6rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '0.875rem',
    background: 'white',
  };

  return (
    <SearchContainer>
      <Navbar />
      <ContentContainer>
        <PageTitle>Search Analyses</PageTitle>

        <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.5rem' }}>
          {['query', 'form'].map(mode => (
            <button
              key={mode}
              type="button"
              onClick={() => setSearchMode(mode)}
              style={{
                padding: '0.35rem 0.8rem', border: '1px solid #d1d5db', borderRadius: '4px',
                background: searchMode === mode ? '#3498db' : 'white',
                color: searchMode === mode ? 'white' : '#374151',
                cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem', textTransform: 'capitalize',
              }}
            >
              {mode}
            </button>
          ))}
        </div>

        <SearchForm onSubmit={searchMode === 'form' ? handleFormSearch : handleSearch}>
          {searchMode === 'query' ? (
            <>
              <SearchBox>
                <SearchInput
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder='e.g.  package_name:"com.evil" & tag:virustotal  |  NOT tag:urlscan'
                />
                <SearchButton type="submit" disabled={loading}>
                  {loading ? 'Loading...' : 'Search'}
                </SearchButton>
                {(inputValue || searchQuery) && (
                  <SearchButton type="button" onClick={handleClear}>
                    Clear
                  </SearchButton>
                )}
                <button
                  type="button"
                  onClick={() => setFiltersOpen(o => !o)}
                  style={{
                    padding: '0.5rem 0.9rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    background: activeCount > 0 ? '#e0e7ff' : 'white',
                    color: activeCount > 0 ? '#3730a3' : '#374151',
                    cursor: 'pointer',
                    fontWeight: '500',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                  }}
                >
                  Filters
                  {activeCount > 0 && (
                    <span style={{
                      background: '#3730a3', color: 'white',
                      borderRadius: '9999px', padding: '0 0.45rem',
                      fontSize: '0.75rem', fontWeight: '700',
                    }}>
                      {activeCount}
                    </span>
                  )}
                </button>
              </SearchBox>
              <HelpText>
                <HintRow>
                  <span><strong>Fields</strong></span>
                  <span>
                    <FieldChip>sha256</FieldChip>
                    <FieldChip>md5</FieldChip>
                    <FieldChip>package_name</FieldChip>
                    <FieldChip>app_name</FieldChip>
                    <FieldChip>malware_family</FieldChip>
                    <FieldChip>tag</FieldChip>
                    <FieldChip>status</FieldChip>
                    <FieldChip>packer</FieldChip>
                    <FieldChip title="Certificate SHA1">cert</FieldChip>
                  </span>
                </HintRow>
                <HintRow>
                  <span><strong>AND</strong></span>
                  <span>
                    <code>app_name:"My App" &amp; tag:virustotal</code>
                    <Note>quote values with spaces</Note>
                  </span>
                </HintRow>
                <HintRow>
                  <span><strong>OR</strong></span>
                  <span><code>tag:virustotal | tag:urlscan</code></span>
                </HintRow>
                <HintRow>
                  <span><strong>NOT</strong></span>
                  <span><code>NOT tag:virustotal</code></span>
                </HintRow>
              </HelpText>
            </>
          ) : (
            <FormPanel>
              <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#6b7280', marginBottom: '1rem' }}>
                Match all filled-in fields (AND)
              </div>
              <FormGrid>
                {FORM_FIELDS.map(({ key, label }) => (
                  <FormField key={key}>
                    <label htmlFor={`form-${key}`}>{label}</label>
                    <input
                      id={`form-${key}`}
                      type="text"
                      value={formFields[key]}
                      onChange={e => setFormFields(f => ({ ...f, [key]: e.target.value }))}
                    />
                  </FormField>
                ))}
              </FormGrid>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
                <SearchButton type="submit" disabled={loading}>
                  {loading ? 'Loading...' : 'Search'}
                </SearchButton>
                <SearchButton type="button" onClick={handleClearForm}>
                  Clear
                </SearchButton>
              </div>
            </FormPanel>
          )}

        </SearchForm>


        {/* Collapsible Filter Panel */}
        {filtersOpen && (
          <div style={{
            background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px',
            padding: '1rem 1.25rem', marginBottom: '1rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
          }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.8rem', fontWeight: '600', color: '#374151' }}>
                Status
                <select style={selectStyle} value={pendingFilters.status} onChange={e => setPendingFilters(f => ({ ...f, status: e.target.value }))}>
                  <option value="">All</option>
                  <option value="completed">Completed</option>
                  <option value="analyzing">Analyzing</option>
                  <option value="failed">Failed</option>
                  <option value="pending">Pending</option>
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.8rem', fontWeight: '600', color: '#374151' }}>
                Packer
                <select style={selectStyle} value={pendingFilters.packer} onChange={e => setPendingFilters(f => ({ ...f, packer: e.target.value }))}>
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.8rem', fontWeight: '600', color: '#374151' }}>
                Frosting
                <select style={selectStyle} value={pendingFilters.frosting} onChange={e => setPendingFilters(f => ({ ...f, frosting: e.target.value }))}>
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.8rem', fontWeight: '600', color: '#374151' }}>
                Tags
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem 0.75rem', maxWidth: '360px' }}>
                  {predefinedTags.map(t => (
                    <label key={t} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer', fontWeight: 'normal', fontSize: '0.85rem' }}>
                      <input
                        type="checkbox"
                        checked={pendingFilters.tag.includes(t)}
                        onChange={e => {
                          const checked = e.target.checked;
                          setPendingFilters(f => ({
                            ...f,
                            tag: checked ? [...f.tag, t] : f.tag.filter(x => x !== t),
                          }));
                        }}
                      />
                      {t}
                    </label>
                  ))}
                </div>
                <CustomTagInput pendingFilters={pendingFilters} setPendingFilters={setPendingFilters} predefinedTags={predefinedTags} />
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.8rem', fontWeight: '600', color: '#374151' }}>
                Last N minutes
                <input
                  type="number"
                  min="1"
                  placeholder="e.g. 60"
                  style={{ ...selectStyle, width: '100px' }}
                  value={pendingFilters.minutes}
                  onChange={e => setPendingFilters(f => ({ ...f, minutes: e.target.value }))}
                />
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={handleApplyFilters}
                  style={{
                    padding: '0.45rem 1rem', background: '#3b82f6', color: 'white',
                    border: 'none', borderRadius: '4px', cursor: 'pointer',
                    fontWeight: '600', fontSize: '0.875rem',
                  }}
                >
                  Apply Filters
                </button>
                <button
                  type="button"
                  onClick={handleClearFilters}
                  style={{
                    padding: '0.45rem 0.75rem', background: 'none', color: '#6b7280',
                    border: 'none', cursor: 'pointer', fontSize: '0.875rem',
                    textDecoration: 'underline',
                  }}
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Active filter chips */}
        {activeCount > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.75rem' }}>
            {Object.entries(filters).map(([key, value]) => {
              if (Array.isArray(value)) {
                return value.map(tag => (
                  <span
                    key={`tag-${tag}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                      padding: '0.2rem 0.6rem', background: '#e0e7ff', color: '#3730a3',
                      borderRadius: '9999px', fontSize: '0.8rem', fontWeight: '500',
                    }}
                  >
                    Tag: {tag}
                    <button
                      onClick={() => handleDismissTag(tag)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', color: '#3730a3', fontWeight: '700', lineHeight: 1 }}
                    >
                      ×
                    </button>
                  </span>
                ));
              }
              return value ? (
                <span
                  key={key}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                    padding: '0.2rem 0.6rem', background: '#e0e7ff', color: '#3730a3',
                    borderRadius: '9999px', fontSize: '0.8rem', fontWeight: '500',
                  }}
                >
                  {FILTER_LABELS[key]}: {value}
                  <button
                    onClick={() => handleDismissChip(key)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', color: '#3730a3', fontWeight: '700', lineHeight: 1 }}
                  >
                    ×
                  </button>
                </span>
              ) : null;
            })}
          </div>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {loading ? (
          <LoadingSpinner>Loading analyses...</LoadingSpinner>
        ) : (
          <>
            <ResultsTable>
              <thead>
                <tr>
                  <Th>Icon</Th>
                  <Th>SHA256</Th>
                  <Th>Package Name</Th>
                  <Th>App Name</Th>
                  <Th>Malware Family</Th>
                  <Th>Status</Th>
                  <Th>Analysis Date</Th>
                  <Th>Tags</Th>
                  <Th>Intelligence</Th>
                </tr>
              </thead>
              <tbody>
                {analyses.length > 0 ? (
                  analyses.map(analysis => (
                    <SearchTableRow
                      key={analysis.sha256}
                      analysis={analysis}
                    />
                  ))
                ) : (
                  <tr>
                    <Th colSpan="9" style={{ textAlign: 'center' }}>
                      No analyses found
                    </Th>
                  </tr>
                )}
              </tbody>
            </ResultsTable>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalResults={totalResults}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </ContentContainer>
    </SearchContainer>
  );
};

export default Search;